import { z } from 'zod';

/**
 * Lot AF [5, 23] (retours du 14/10/2026) : titres des rendez-vous clients R1-R5 et invitation du client.
 * - `settings.meetings.titles` : titre de chaque rendez-vous, `{client}` remplacé par le nom du client. Le plan
 *   « Fixer le rendez-vous » (`meeting.schedule`) l'utilise ; la synchro des agendas reconnaît aussi un titre qui
 *   commence par la partie fixe du modèle (le texte avant `{client}`, casse et accents ignorés), en plus du
 *   préfixe R1 (`settings.calendar.r1Prefix`) et de l'ancien format « R2 … » à « R5 … ».
 * - `settings.meetings.inviteClient` : la case « Inviter le client » du plan est cochée par défaut (R2-R5).
 *   R1 est toujours posé à la main (lecture seule), d'où l'absence de case.
 */

export const MEETING_TITLE_KINDS = ['R1', 'R2', 'R3', 'R4', 'R5'] as const;
export type MeetingTitleKind = (typeof MEETING_TITLE_KINDS)[number];
export const MEETING_INVITE_KINDS = ['R2', 'R3', 'R4', 'R5'] as const;
export type MeetingInviteKind = (typeof MEETING_INVITE_KINDS)[number];

/** Le marqueur remplacé par le nom du client. */
export const CLIENT_PLACEHOLDER = '{client}';

export const DEFAULT_MEETING_TITLES: Record<MeetingTitleKind, string> = {
  R1: 'Rendez-vous Découverte Evolusite x {client}',
  R2: 'Présentation du devis Evolusite x {client}',
  R3: 'Prise de contenu pour le site internet Evolusite x {client}',
  R4: 'Présentation de la maquette Evolusite x {client}',
  R5: 'Rendu du site Evolusite x {client}',
};

/** Comparaison des titres : sans accents, sans casse, espaces réduits (comme `normalizeText` du serveur). */
export function normalizeMeetingText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u00a0\u202f]/g, ' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Partie fixe d'un modèle : le texte avant `{client}` (« Rendu du site Evolusite x »). */
export function meetingTemplatePrefix(template: string): string {
  const at = template.indexOf(CLIENT_PLACEHOLDER);
  return (at >= 0 ? template.slice(0, at) : template).trim();
}

/** Une partie fixe trop courte reconnaîtrait n'importe quel événement : au moins 3 lettres ou chiffres. */
export const MIN_TEMPLATE_PREFIX_CHARS = 3;

const MeetingTitle = z
  .string()
  .trim()
  .min(1, { message: 'Titre obligatoire' })
  .max(200)
  .refine((t) => t.split(CLIENT_PLACEHOLDER).length === 2, {
    message: 'Le titre doit contenir {client} une fois',
  })
  .refine(
    (t) =>
      normalizeMeetingText(meetingTemplatePrefix(t)).replace(/[^a-z0-9]/g, '').length >=
      MIN_TEMPLATE_PREFIX_CHARS,
    { message: 'Le titre doit commencer par un texte fixe avant {client} (3 lettres au moins)' },
  );

export const MeetingSettings = z
  .object({
    titles: z.object({
      R1: MeetingTitle,
      R2: MeetingTitle,
      R3: MeetingTitle,
      R4: MeetingTitle,
      R5: MeetingTitle,
    }),
    inviteClient: z.object({ R2: z.boolean(), R3: z.boolean(), R4: z.boolean(), R5: z.boolean() }),
  })
  .superRefine((value, ctx) => {
    // Deux modèles de même partie fixe : impossible de savoir quel rendez-vous est lequel.
    const seen = new Map<string, MeetingTitleKind>();
    for (const kind of MEETING_TITLE_KINDS) {
      const prefix = normalizeMeetingText(meetingTemplatePrefix(value.titles[kind]));
      const other = seen.get(prefix);
      if (other) {
        ctx.addIssue({
          code: 'custom',
          path: ['titles', kind],
          message: `Même début de titre que le ${other} : le Cockpit ne saurait pas les distinguer`,
        });
      } else seen.set(prefix, kind);
    }
  });
export type MeetingSettings = z.infer<typeof MeetingSettings>;

export const DEFAULT_MEETING_SETTINGS: MeetingSettings = {
  titles: { ...DEFAULT_MEETING_TITLES },
  inviteClient: { R2: true, R3: true, R4: true, R5: true },
};

/** Titre d'un rendez-vous pour un client : « Rendu du site Evolusite x Garage des Sables ». */
export function meetingTitle(template: string, client: string): string {
  return template.split(CLIENT_PLACEHOLDER).join(client.trim()).replace(/\s+/g, ' ').trim();
}
